/**
 * StatusPill — pill-shaped indicator for the status of a domain entity.
 *
 * One primitive, five semantic variants. Pair with a leading dot for
 * "in-progress" / "active" connotations (use `dot` prop). The label is
 * always rendered as the children — feature code provides domain copy
 * (e.g., "running", "done") and chooses a variant.
 *
 * Variants:
 *   info    — neutral or in-progress state (e.g., running)
 *   success — affirmative completion (e.g., done)
 *   warning — needs-attention state
 *   danger  — failure (e.g., failed)
 *   muted   — neutral terminated state (e.g., cancelled)
 */

import { forwardRef, type HTMLAttributes } from 'react';
import styles from './StatusPill.module.css';

type StatusVariant = 'info' | 'success' | 'warning' | 'danger' | 'muted';

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: StatusVariant;
  /** Render a leading dot — common for "active" / "in-progress" states. */
  dot?: boolean;
}

const VARIANT_CLASS: Record<StatusVariant, string | undefined> = {
  info: styles.variantInfo,
  success: styles.variantSuccess,
  warning: styles.variantWarning,
  danger: styles.variantDanger,
  muted: styles.variantMuted,
};

export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(function StatusPill(
  { variant = 'info', dot = false, className, children, ...rest },
  ref,
) {
  const composedClassName = [styles.pill, VARIANT_CLASS[variant], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <span ref={ref} className={composedClassName} {...rest}>
      {dot ? <span className={styles.dot} aria-hidden="true" /> : null}
      {children}
    </span>
  );
});
